import { useAppStore } from '../stores/useAppStore';
import type {
  Chat,
  Settings,
  Slug,
  Story,
  Universe,
} from '../types';
import { resumePushSync, suspendPushSync } from './pushSync';
import { listObjectsBulk, syncMe } from './syncClient';

export type BootstrapPhase =
  | 'auth'
  | 'settings'
  | 'universes'
  | 'slugs'
  | 'stories'
  | 'chats';

export type BootstrapProgress = {
  phase: BootstrapPhase;
  done: number;
  total: number;
  message?: string;
};

export type BootstrapResult = {
  universes: number;
  slugs: number;
  stories: number;
  chats: number;
};

export type BootstrapOptions = {
  onProgress?: (p: BootstrapProgress) => void;
  onLog?: (line: string) => void;
};

export async function bootstrapFromServer(
  opts: BootstrapOptions = {}
): Promise<BootstrapResult> {
  const onProgress = opts.onProgress ?? (() => {});
  const onLog = opts.onLog ?? (() => {});

  suspendPushSync();
  try {
    return await runBootstrap(onProgress, onLog);
  } finally {
    resumePushSync();
  }
}

async function runBootstrap(
  onProgress: (p: BootstrapProgress) => void,
  onLog: (line: string) => void
): Promise<BootstrapResult> {
  onProgress({ phase: 'auth', done: 0, total: 1 });
  const me = await syncMe();
  onLog(`Authed as ${me.user}.`);
  onProgress({ phase: 'auth', done: 1, total: 1 });

  onProgress({ phase: 'settings', done: 0, total: 1 });
  const settingsItems = await listObjectsBulk<Partial<Settings>>('settings');
  const remoteSettings = settingsItems.find((it) => it.id === 'global')?.json;
  if (remoteSettings) {
    const cur = useAppStore.getState().settings;
    useAppStore.setState({
      settings: {
        ...cur,
        ...remoteSettings,
        // Server doesn't store these — preserve whatever's local:
        apiBaseUrl: cur.apiBaseUrl,
        apiToken: cur.apiToken,
      } as Settings,
    });
    onLog('Settings restored.');
  }
  onProgress({ phase: 'settings', done: 1, total: 1 });

  const universeItems = await listObjectsBulk<Universe>('universe');
  const universes: Record<string, Universe> = {};
  for (const it of universeItems) universes[it.id] = it.json;
  useAppStore.setState({ universes });
  onProgress({
    phase: 'universes',
    done: universeItems.length,
    total: universeItems.length,
  });
  onLog(`Universes: ${universeItems.length}.`);

  const slugItems = await listObjectsBulk<Slug>('slug');
  const slugPool = slugItems.map((it) => it.json);
  useAppStore.setState({ slugPool });
  onProgress({ phase: 'slugs', done: slugItems.length, total: slugItems.length });
  onLog(`Slugs: ${slugItems.length}.`);

  const storyItems = await listObjectsBulk<Story>('story');
  const stories: Record<string, Story> = {};
  for (const it of storyItems) stories[it.id] = it.json;
  useAppStore.setState({ stories });
  onProgress({
    phase: 'stories',
    done: storyItems.length,
    total: storyItems.length,
  });
  onLog(`Stories: ${storyItems.length}.`);

  const chatItems = await listObjectsBulk<Chat>('chat');
  const chatsMap: Record<string, Chat> = {};
  for (const it of chatItems) chatsMap[it.id] = it.json;
  useAppStore.setState({ chats: chatsMap });
  onProgress({ phase: 'chats', done: chatItems.length, total: chatItems.length });
  onLog(`Chats: ${chatItems.length}.`);

  return {
    universes: universeItems.length,
    slugs: slugItems.length,
    stories: storyItems.length,
    chats: chatItems.length,
  };
}

/**
 * Auto-bootstrap on cold start: if a sync token is configured AND zustand
 * has no universes (fresh device or new host URL), pull everything from the
 * server. Idempotent and safe to call on every boot.
 *
 * Always transitions bootstrapStatus to 'ready' by the time it returns,
 * regardless of which branch ran — so onboarding/UX code that waits on
 * "we've made our decision" can subscribe to that single flag.
 */
export async function maybeAutoBootstrap(): Promise<void> {
  const state = useAppStore.getState();
  try {
    if (!state.settings.apiToken) return;
    if (Object.keys(state.universes).length > 0) return;
    console.info('[bootstrap] empty store + token set — restoring from server');
    const r = await bootstrapFromServer({
      onLog: (line) => console.info('[bootstrap]', line),
    });
    console.info('[bootstrap] done', r);
  } catch (e) {
    console.warn('[bootstrap] failed', e);
  } finally {
    useAppStore.getState().setBootstrapStatus('ready');
  }
}
